import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CircleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CarePageHeader from '@/components/CarePageHeader';
import MetaTags from '@/components/SEO/MetaTags';
import {
  DollarSign,
  Calculator,
  Building2,
  User,
  Syringe,
  Plane,
  Shield,
} from 'lucide-react';
import { countriesData, insurersData, cancerTypes, stages, hospitalTiers } from '@/lib/costCalculatorData';
import { apiUrl } from '@/config/api';
import { useVaniForm } from '@/voice/useVaniForm';

const CALCULATE_URL = apiUrl('/api/cost-calculator/calculate-cost');
const INPUT_STEPS = 6; // 1-6 = form, 7 = results
const SELECT_TRIGGER =
  'w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl px-4 focus:ring-0 focus:border-forest-ink text-sm';
const SELECT_CONTENT =
  'by-select-content bg-cream-paper border-hair text-forest-ink p-1.5 max-h-72 overflow-y-auto rounded-2xl shadow-lg';
const SELECT_ITEM =
  'text-forest-ink cursor-pointer px-3 py-2.5 my-0.5 rounded-xl focus:bg-mist focus:text-forest-ink focus:outline-none data-[highlighted]:bg-mist data-[highlighted]:text-forest-ink';
const FIELD_INPUT =
  'w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl px-4 placeholder:text-charcoal focus-visible:ring-0 focus:border-forest-ink text-sm';

function RequiredMark() {
  return <span className="text-forest-ink" aria-hidden="true"> *</span>;
}

function getStepIssues(formData, step) {
  const items = [];
  if (step === 1) {
    if (!formData.country) items.push('Destination country');
    if (!formData.hospitalTier) items.push('Hospital type');
  }
  if (step === 2) {
    if (!formData.ageGroup) items.push('Age group');
    if (!formData.cancerCategory) items.push('Cancer category');
    if (!formData.cancerType) items.push('Disease type');
    if (!formData.stage) items.push('Stage');
    if (!formData.intent) items.push('Treatment intent');
  }
  if (step === 3) {
    if (!formData.includeSurgery && !formData.includeChemo && !formData.includeRadiation && !formData.includeTransplant) {
      items.push('At least one treatment');
    }
    if (formData.includeSurgery && !formData.surgeryType) items.push('Surgery type');
    if (formData.includeChemo && !formData.regimenType) items.push('Chemotherapy regimen');
    if (formData.includeRadiation && !formData.radiationTechnique) items.push('Radiation technique');
    if (formData.includeTransplant && !formData.transplantType) items.push('Transplant type');
  }
  if (step === 4) {
    if (!formData.followUpMonths) items.push('Follow-up horizon');
  }
  if (step === 5) {
    if (!formData.paymentMethod) items.push('How you will pay');
    if (formData.paymentMethod === 'insurance') {
      if (!formData.insurer) items.push('Insurance provider');
      if (!formData.policyType) items.push('Policy type');
    }
  }
  if (step === 6) {
    if (!formData.accommodationLevel) items.push('Accommodation level');
    if (!formData.travelType) items.push('Travel type');
    if (!formData.localTransport) items.push('Local transport');
  }
  return items;
}

function getAllIssues(formData) {
  return [1, 2, 3, 4, 5, 6].flatMap((step) => getStepIssues(formData, step));
}

function formatMoney(value, digits = 2) {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  const safe = Number.isNaN(num) ? 0 : num;
  return safe.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function CostNotice({ title, items, detail }) {
  if (!items?.length && !detail) return null;
  return (
    <div className="cost-notice" role="alert">
      <CircleAlert className="cost-notice-icon" aria-hidden="true" />
      <div className="cost-notice-body">
        <p className="cost-notice-title">{title}</p>
        {detail ? <p className="cost-notice-detail">{detail}</p> : null}
        {items?.length ? (
          <ul className="cost-notice-list">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function CostPreviewPanel({ formData, costResult, selectedCountryData }) {
  const treatments = [
    formData.includeSurgery && 'Surgery',
    formData.includeChemo && 'Chemotherapy',
    formData.includeRadiation && 'Radiation',
    formData.includeTransplant && 'Transplant',
  ].filter(Boolean);
  const country = selectedCountryData?.name || 'Choose a destination';
  const total = costResult?.totalCostUSD;
  const symbol = costResult?.currencySymbol || '$';
  const local = costResult?.totalCostLocal;

  return (
    <div className="cost-preview">
      <div className="cost-stage" aria-hidden="true">
        <div className="cost-wash" />
        <div className="cost-atmosphere" />
        <div className="cost-ring cost-ring-1" />
        <div className="cost-ring cost-ring-2" />
        <div className="cost-ring cost-ring-3" />
        <div className="cost-orb">
          <div className="cost-orb-core" />
          <div className="cost-orb-map" />
          <div className="cost-orb-shade" />
          <div className="cost-orb-gloss" />
          <div className="cost-orb-rim" />
        </div>
        <div className="cost-contact" />
      </div>
      <div className="cost-preview-copy">
        <div>
          <p className="cost-kicker">Live estimate</p>
          <h3 className="cost-title">{country}</h3>
          {formData.city ? <p className="cost-meta">{formData.city}</p> : null}
          {formData.hospitalTier ? (
            <p className="cost-meta">
              {hospitalTiers.find((t) => t.id === formData.hospitalTier)?.name || formData.hospitalTier}
            </p>
          ) : null}
        </div>
        <div className="cost-preview-foot">
          {treatments.length > 0 ? (
            <div className="cost-chips">
              {treatments.map((t) => (
                <span key={t} className="cost-chip">{t}</span>
              ))}
            </div>
          ) : (
            <p className="cost-hint">
              Select a country, hospital type, and at least one treatment to generate a country-level estimate.
            </p>
          )}
          {total ? (
            <div>
              <p className="cost-kicker">Estimated total</p>
              <p className="cost-total">
                {symbol}{formatMoney(local || total, 0)}
              </p>
              {costResult?.totalCostUSD != null && (
                <p className="cost-meta">
                  ${formatMoney(costResult.totalCostUSD, 0)} USD
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const createInitialFormData = () => ({
  // Country & Hospital
  country: 'usa',
  city: '',
  hospitalTier: '',
  accreditation: [],

  // Patient & Disease
  ageGroup: '',
  cancerCategory: '',
  cancerType: '',
  stage: '',
  intent: '',

  // Treatment - Surgery
  includeSurgery: false,
  surgeryType: '',
  surgeryDays: 3,
  icuDays: 0,
  roomCategory: 'semi_private',

  // Treatment - Chemotherapy
  includeChemo: false,
  regimenType: '',
  chemoCycles: 6,
  drugAccess: 'generics',

  // Treatment - Radiation
  includeRadiation: false,
  radiationTechnique: '',
  radiationFractions: 25,
  concurrentChemo: false,

  // Treatment - Transplant
  includeTransplant: false,
  transplantType: '',
  transplantDays: 30,

  // Diagnostics
  petCtCount: 2,
  mriCtCount: 4,
  includeNGS: false,
  opdConsults: 10,
  followUpMonths: 12,

  // Insurance
  hasInsurance: false,
  paymentMethod: '',
  insurer: '',
  policyType: '',
  customCoverage: false,
  inpatientCoverage: 80,
  outpatientCoverage: 50,
  drugCoverage: 70,
  deductible: 0,
  copayPercent: 20,

  // Medical Tourism
  companions: 1,
  stayDuration: 60,
  accommodationLevel: '',
  travelType: '',
  returnTrips: 1,
  localTransport: '',

  // Admin
  complicationBuffer: 15,
  currency: 'USD',
});

const CostCalculator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(createInitialFormData);
  const [costResult, setCostResult] = useState(null);
  const [selectedCountryData, setSelectedCountryData] = useState(null);
  const [availableInsurers, setAvailableInsurers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [missingItems, setMissingItems] = useState([]);

  // When country changes, sync currency + insurers
  useEffect(() => {
    if (formData.country) {
      const country = countriesData.find((c) => c.id === formData.country) || null;
      setSelectedCountryData(country);
      setAvailableInsurers(insurersData[formData.country] || []);
      setFormData((prev) => ({
        ...prev,
        currency: country?.currency || 'USD',
      }));
    } else {
      setSelectedCountryData(null);
      setAvailableInsurers([]);
    }
  }, [formData.country]);

  const handleInputChange = (field, value) => {
    setMissingItems([]);
    setErrorMessage('');
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'paymentMethod') {
        next.hasInsurance = value === 'insurance';
        if (value !== 'insurance') {
          next.insurer = '';
          next.customCoverage = false;
        }
      }
      if (field === 'hasInsurance') {
        next.paymentMethod = value ? 'insurance' : 'self_pay';
        if (!value) {
          next.insurer = '';
          next.customCoverage = false;
        }
      }
      return next;
    });
  };

  const validateBeforeCalculate = () => {
    const missing = getAllIssues(formData);
    if (missing.length) {
      setMissingItems(missing);
      setErrorMessage('');
      return false;
    }
    setMissingItems([]);
    setErrorMessage('');
    return true;
  };

  const buildRequestPayload = () => {
    return {
      // Country & Hospital
      country: formData.country,
      city: formData.city || null,
      hospital_tier: formData.hospitalTier,
      accreditation: formData.accreditation,

      // Patient & Disease
      age_group: formData.ageGroup,
      cancer_category: formData.cancerCategory,
      cancer_type: formData.cancerType,
      stage: formData.stage,
      intent: formData.intent,

      // Treatment - Surgery
      include_surgery: formData.includeSurgery,
      surgery_type: formData.surgeryType || null,
      surgery_days: formData.surgeryDays,
      icu_days: formData.icuDays,
      room_category: formData.roomCategory,

      // Treatment - Chemotherapy
      include_chemo: formData.includeChemo,
      regimen_type: formData.regimenType || null,
      chemo_cycles: formData.chemoCycles,
      drug_access: formData.drugAccess,

      // Treatment - Radiation
      include_radiation: formData.includeRadiation,
      radiation_technique: formData.radiationTechnique || null,
      radiation_fractions: formData.radiationFractions,
      concurrent_chemo: formData.concurrentChemo,

      // Treatment - Transplant
      include_transplant: formData.includeTransplant,
      transplant_type: formData.transplantType || null,
      transplant_days: formData.transplantDays,

      // Diagnostics
      pet_ct_count: formData.petCtCount,
      mri_ct_count: formData.mriCtCount,
      include_ngs: formData.includeNGS,
      opd_consults: formData.opdConsults,
      follow_up_months: formData.followUpMonths,

      // Insurance
      has_insurance: formData.hasInsurance,
      insurer: formData.insurer || null,
      policy_type: formData.policyType || 'domestic',
      custom_coverage: formData.customCoverage,
      inpatient_coverage: formData.inpatientCoverage,
      outpatient_coverage: formData.outpatientCoverage,
      drug_coverage: formData.drugCoverage,
      deductible: formData.deductible,
      copay_percent: formData.copayPercent,

      // Medical Tourism
      companions: formData.companions,
      stay_duration: formData.stayDuration,
      accommodation_level: formData.accommodationLevel,
      travel_type: formData.travelType,
      return_trips: formData.returnTrips,
      local_transport: formData.localTransport,

      // Admin
      complication_buffer: formData.complicationBuffer,
      currency: formData.currency,
    };
  };

  const calculateCost = async () => {
    if (!validateBeforeCalculate()) return;

    try {
      setIsLoading(true);
      setErrorMessage('');

      const payload = buildRequestPayload();

      const response = await fetch(CALCULATE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(() => {
            const t = localStorage.getItem('byonco_jwt');
            return t ? { Authorization: `Bearer ${t}` } : {};
          })(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Handle subscription/authentication errors
        const { handleApiError } = await import('@/utils/apiErrorHandler');
        const { handled } = await handleApiError(response, navigate);
        if (handled) {
          setIsLoading(false);
          return;
        }

        // Log the full error for debugging
        const status = response.status;
        const errorBody = await response.json().catch(() => ({}));
        const errorMessage = errorBody.detail || errorBody.message || `HTTP ${status}: Failed to calculate cost.`;
        
        // Log to console for debugging
        console.error('Cost calculator API error:', {
          status,
          statusText: response.statusText,
          url: CALCULATE_URL,
          error: errorBody,
          payload: payload
        });
        
        if (status === 404) {
          throw new Error('The cost service is temporarily unavailable. Please try again in a moment.');
        }

        throw new Error(typeof errorMessage === 'string' ? errorMessage : 'We could not calculate this estimate. Please review your selections and try again.');
      }

      const data = await response.json();

      // Map backend snake_case fields into camelCase for UI with safe fallbacks
      const safeNumber = (val) => {
        const num = typeof val === 'number' ? val : parseFloat(val) || 0;
        return isNaN(num) ? 0 : Math.max(0, num);
      };

      const safeBreakdown = data.breakdown || {};
      const safeBreakdownUSD = data.breakdown_usd || {};
      const mappedResult = {
        totalCostLocal: safeNumber(data.total_cost_local),
        totalCostUSD: safeNumber(data.total_cost_usd),
        totalCostINR: safeNumber(data.total_cost_inr),
        clinicalCost: safeNumber(data.clinical_cost),
        clinicalCostUSD: safeNumber(data.clinical_cost_usd),
        nonClinicalCost: safeNumber(data.non_clinical_cost),
        nonClinicalCostUSD: safeNumber(data.non_clinical_cost_usd),
        insurancePays: safeNumber(data.insurance_pays),
        insurancePaysUSD: safeNumber(data.insurance_pays_usd),
        patientOutOfPocket: safeNumber(data.patient_out_of_pocket),
        patientOutOfPocketUSD: safeNumber(data.patient_out_of_pocket_usd),
        breakdown: {
          surgery: safeNumber(safeBreakdown.surgery),
          chemotherapy: safeNumber(safeBreakdown.chemotherapy),
          radiation: safeNumber(safeBreakdown.radiation),
          transplant: safeNumber(safeBreakdown.transplant),
          diagnostics: safeNumber(safeBreakdown.diagnostics),
          accommodation: safeNumber(safeBreakdown.accommodation),
          travel: safeNumber(safeBreakdown.travel),
          localTransport: safeNumber(safeBreakdown.local_transport),
          food: safeNumber(safeBreakdown.food),
        },
        breakdownUSD: {
          surgery: safeNumber(safeBreakdownUSD.surgery),
          chemotherapy: safeNumber(safeBreakdownUSD.chemotherapy),
          radiation: safeNumber(safeBreakdownUSD.radiation),
          transplant: safeNumber(safeBreakdownUSD.transplant),
          diagnostics: safeNumber(safeBreakdownUSD.diagnostics),
          accommodation: safeNumber(safeBreakdownUSD.accommodation),
          travel: safeNumber(safeBreakdownUSD.travel),
          localTransport: safeNumber(safeBreakdownUSD.local_transport),
          food: safeNumber(safeBreakdownUSD.food),
        },
        currencyCode: data.currency_code || data.currency || formData.currency || 'USD',
        currencySymbol: data.currency_symbol || '$',
        exchangeRateToUSD: safeNumber(data.exchange_rate_to_usd),
        assumptions: Array.isArray(data.assumptions) ? data.assumptions : [],
      };

      setCostResult(mappedResult);
      setStep(7);
    } catch (err) {
      console.error('Error calculating cost:', err);
      setErrorMessage(err.message || 'Something went wrong while calculating cost.');
    } finally {
      setIsLoading(false);
    }
  };

  useVaniForm('/cost-calculator', {
    fill: (fields) => {
      setFormData((prev) => {
        const next = { ...prev };
        Object.keys(fields || {}).forEach((k) => {
          if (k in prev) next[k] = fields[k];
        });
        return next;
      });
    },
    search: () => { calculateCost(); },
  });

  const resetCalculator = () => {
    setFormData(createInitialFormData());
    setCostResult(null);
    setSelectedCountryData(null);
    setAvailableInsurers([]);
    setErrorMessage('');
    setMissingItems([]);
    setStep(1);
  };

  const displayCurrency = costResult?.currencyCode || costResult?.currency || selectedCountryData?.currency || formData.currency;
  const displayCurrencySymbol = costResult?.currencySymbol || (displayCurrency === 'INR' ? '₹' : displayCurrency === 'USD' ? '$' : displayCurrency === 'EUR' ? '€' : displayCurrency === 'SGD' ? 'S$' : displayCurrency === 'JPY' ? '¥' : displayCurrency === 'TRY' ? '₺' : displayCurrency === 'THB' ? '฿' : displayCurrency === 'CAD' ? 'C$' : displayCurrency === 'NOK' ? 'kr' : '$');

  // Helper function to safely convert values to numbers
  const safeNumber = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return isNaN(num) ? 0 : Math.max(0, num);
  };

  const renderStep1 = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-forest-ink flex-shrink-0" />
        <h2 className="text-forest-ink break-words">
          Country and hospital details
        </h2>
      </div>

      <div className="space-y-6 sm:space-y-7">
        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Destination country<RequiredMark /></Label>
          <Select
            value={formData.country}
            onValueChange={(val) => handleInputChange('country', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select country" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              {countriesData.map((country) => (
                <SelectItem key={country.id} value={country.id} className={SELECT_ITEM}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink font-medium">City / region (optional)</Label>
            <Input
              placeholder="Enter city name"
              className={FIELD_INPUT}
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
            />
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Hospital type<RequiredMark /></Label>
          <Select
            value={formData.hospitalTier}
            onValueChange={(val) => handleInputChange('hospitalTier', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select hospital tier" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              {hospitalTiers.map((tier) => (
                <SelectItem key={tier.id} value={tier.id} className={SELECT_ITEM}>
                  {tier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Accreditations (optional)</Label>
          <div className="space-y-2 sm:space-y-3 mt-2">
            {['JCI', 'NABH', 'NCQA', 'ISO'].map((acc) => (
              <div key={acc} className="flex items-center space-x-2 sm:space-x-3">
                <Checkbox
                  id={acc}
                  checked={formData.accreditation.includes(acc)}
                  onCheckedChange={(checked) => {
                    const isChecked = !!checked;
                    if (isChecked) {
                      handleInputChange('accreditation', [
                        ...formData.accreditation,
                        acc,
                      ]);
                    } else {
                      handleInputChange(
                        'accreditation',
                        formData.accreditation.filter((a) => a !== acc)
                      );
                    }
                  }}
                />
                <label htmlFor={acc} className="text-xs sm:text-sm cursor-pointer text-forest-ink">
                  {acc}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <User className="w-5 h-5 sm:w-6 sm:h-6 text-forest-ink flex-shrink-0" />
        <h2 className="text-forest-ink break-words">
          Patient & Disease Information
        </h2>
      </div>

      <div className="space-y-6 sm:space-y-7">
        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Age group<RequiredMark /></Label>
          <Select
            value={formData.ageGroup}
            onValueChange={(val) => handleInputChange('ageGroup', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select age group" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="pediatric" className={SELECT_ITEM}>Pediatric (&lt;18)</SelectItem>
              <SelectItem value="adult" className={SELECT_ITEM}>Adult (18-65)</SelectItem>
              <SelectItem value="geriatric" className={SELECT_ITEM}>Geriatric (&gt;65)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Cancer category<RequiredMark /></Label>
          <Select
            value={formData.cancerCategory}
            onValueChange={(val) => handleInputChange('cancerCategory', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select cancer category" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="common" className={SELECT_ITEM}>Common</SelectItem>
              <SelectItem value="rare" className={SELECT_ITEM}>Rare</SelectItem>
              <SelectItem value="ultra_rare" className={SELECT_ITEM}>Ultra-rare</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink font-medium">Disease type<RequiredMark /></Label>
          <Select
            value={formData.cancerType}
            onValueChange={(val) => handleInputChange('cancerType', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select cancer type" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              {cancerTypes.map((cancer) => (
                <SelectItem key={cancer.id} value={cancer.id} className={SELECT_ITEM}>
                  {cancer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-3 block text-forest-ink font-medium">Stage<RequiredMark /></Label>
          <Select
            value={formData.stage}
            onValueChange={(val) => handleInputChange('stage', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select stage" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id} className={SELECT_ITEM}>
                  {stage.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-3 block text-forest-ink font-medium">Treatment Intent<RequiredMark /></Label>
          <Select
            value={formData.intent}
            onValueChange={(val) => handleInputChange('intent', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select intent" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="curative" className={SELECT_ITEM}>Curative</SelectItem>
              <SelectItem value="palliative" className={SELECT_ITEM}>Palliative</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Syringe className="w-6 h-6 text-forest-ink" />
        <h2 className="text-forest-ink">
          Treatment Plan
        </h2>
      </div>

      <p className="text-sm text-charcoal">Choose at least one treatment, then complete its details.</p>
      <Tabs defaultValue="surgery" className="w-full">
        <TabsList className="cost-segment h-auto min-h-11 w-full grid grid-cols-2 sm:grid-cols-4 gap-1 rounded-2xl bg-mist p-1 overflow-hidden border border-hair">
          <TabsTrigger value="surgery" className="h-10 min-w-0 rounded-xl px-1 text-xs sm:text-sm font-medium">
            Surgery
          </TabsTrigger>
          <TabsTrigger value="chemo" className="h-10 min-w-0 rounded-xl px-1 text-xs sm:text-sm font-medium">
            Chemo
          </TabsTrigger>
          <TabsTrigger value="radiation" className="h-10 min-w-0 rounded-xl px-1 text-xs sm:text-sm font-medium">
            Radiation
          </TabsTrigger>
          <TabsTrigger value="transplant" className="h-10 min-w-0 rounded-xl px-1 text-xs sm:text-sm font-medium">
            Transplant
          </TabsTrigger>
        </TabsList>

        <TabsContent value="surgery" className="space-y-6 mt-8">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="includeSurgery"
              checked={formData.includeSurgery}
              onCheckedChange={(checked) =>
                handleInputChange('includeSurgery', !!checked)
              }
            />
            <Label htmlFor="includeSurgery" className="text-forest-ink cursor-pointer font-medium">Include Surgery</Label>
          </div>

          {formData.includeSurgery && (
            <div className="space-y-6 pl-4 border-l-2 border-border-mist ml-2 mt-4">
              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Surgery Type<RequiredMark /></Label>
                <Select
                  value={formData.surgeryType}
                  onValueChange={(val) => handleInputChange('surgeryType', val)}
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select surgery type" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="lumpectomy" className={SELECT_ITEM}>Lumpectomy</SelectItem>
                    <SelectItem value="mastectomy" className={SELECT_ITEM}>Mastectomy</SelectItem>
                    <SelectItem value="whipple" className={SELECT_ITEM}>Whipple</SelectItem>
                    <SelectItem value="lobectomy" className={SELECT_ITEM}>Lobectomy</SelectItem>
                    <SelectItem value="crs_hipec" className={SELECT_ITEM}>CRS + HIPEC</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="block text-forest-ink font-medium mb-2">
                  Hospital Stay (days): {formData.surgeryDays}
                </Label>
                <div className="px-2">
                  <Slider
                    value={[formData.surgeryDays]}
                    onValueChange={(val) => handleInputChange('surgeryDays', val[0])}
                    min={1}
                    max={30}
                    step={1}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="block text-forest-ink font-medium mb-2">
                  ICU Stay (days): {formData.icuDays}
                </Label>
                <div className="px-2">
                  <Slider
                    value={[formData.icuDays]}
                    onValueChange={(val) => handleInputChange('icuDays', val[0])}
                    min={0}
                    max={15}
                    step={1}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Room Category</Label>
                <Select
                  value={formData.roomCategory}
                  onValueChange={(val) => handleInputChange('roomCategory', val)}
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select room category" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="general" className={SELECT_ITEM}>General Ward</SelectItem>
                    <SelectItem value="semi_private" className={SELECT_ITEM}>Semi-private</SelectItem>
                    <SelectItem value="private" className={SELECT_ITEM}>Private</SelectItem>
                    <SelectItem value="deluxe" className={SELECT_ITEM}>Deluxe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="chemo" className="space-y-6 mt-8">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="includeChemo"
              checked={formData.includeChemo}
              onCheckedChange={(checked) =>
                handleInputChange('includeChemo', !!checked)
              }
            />
            <Label htmlFor="includeChemo" className="text-forest-ink cursor-pointer font-medium">Include Chemotherapy</Label>
          </div>

          {formData.includeChemo && (
            <div className="space-y-6 pl-4 border-l-2 border-border-mist ml-2 mt-4">
              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Regimen Type<RequiredMark /></Label>
                <Select
                  value={formData.regimenType}
                  onValueChange={(val) => handleInputChange('regimenType', val)}
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select regimen" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="standard_chemo" className={SELECT_ITEM}>Standard Chemotherapy</SelectItem>
                    <SelectItem value="targeted" className={SELECT_ITEM}>Targeted Therapy</SelectItem>
                    <SelectItem value="immunotherapy" className={SELECT_ITEM}>Immunotherapy (IO)</SelectItem>
                    <SelectItem value="oral_tki" className={SELECT_ITEM}>Oral TKIs</SelectItem>
                    <SelectItem value="combination" className={SELECT_ITEM}>
                      Combination (Chemo + IO)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="block text-forest-ink font-medium mb-2">
                  Number of Cycles: {formData.chemoCycles}
                </Label>
                <div className="px-2">
                  <Slider
                    value={[formData.chemoCycles]}
                    onValueChange={(val) => handleInputChange('chemoCycles', val[0])}
                    min={1}
                    max={24}
                    step={1}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Drug Access</Label>
                <Select
                  value={formData.drugAccess}
                  onValueChange={(val) => handleInputChange('drugAccess', val)}
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select drug access" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="originator" className={SELECT_ITEM}>Originator Only</SelectItem>
                    <SelectItem value="generics" className={SELECT_ITEM}>Generics Allowed</SelectItem>
                    <SelectItem value="biosimilars" className={SELECT_ITEM}>Biosimilars Allowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="radiation" className="space-y-6 mt-8">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="includeRadiation"
              checked={formData.includeRadiation}
              onCheckedChange={(checked) =>
                handleInputChange('includeRadiation', !!checked)
              }
            />
            <Label htmlFor="includeRadiation" className="text-forest-ink cursor-pointer font-medium">Include Radiation Therapy</Label>
          </div>

          {formData.includeRadiation && (
            <div className="space-y-6 pl-4 border-l-2 border-border-mist ml-2 mt-4">
              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Radiation Technique<RequiredMark /></Label>
                <Select
                  value={formData.radiationTechnique}
                  onValueChange={(val) =>
                    handleInputChange('radiationTechnique', val)
                  }
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select technique" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="2d" className={SELECT_ITEM}>2D</SelectItem>
                    <SelectItem value="3d_crt" className={SELECT_ITEM}>3D-CRT</SelectItem>
                    <SelectItem value="imrt" className={SELECT_ITEM}>IMRT</SelectItem>
                    <SelectItem value="vmat" className={SELECT_ITEM}>VMAT</SelectItem>
                    <SelectItem value="sbrt" className={SELECT_ITEM}>SBRT</SelectItem>
                    <SelectItem value="proton" className={SELECT_ITEM}>Proton</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="block text-forest-ink font-medium mb-2">
                  Number of Fractions: {formData.radiationFractions}
                </Label>
                <div className="px-2">
                  <Slider
                    value={[formData.radiationFractions]}
                    onValueChange={(val) =>
                      handleInputChange('radiationFractions', val[0])
                    }
                    min={1}
                    max={40}
                    step={1}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="concurrentChemo"
                  checked={formData.concurrentChemo}
                  onCheckedChange={(checked) =>
                    handleInputChange('concurrentChemo', !!checked)
                  }
                />
                <Label htmlFor="concurrentChemo" className="text-forest-ink cursor-pointer font-medium">Concurrent Chemotherapy</Label>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="transplant" className="space-y-6 mt-8">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="includeTransplant"
              checked={formData.includeTransplant}
              onCheckedChange={(checked) =>
                handleInputChange('includeTransplant', !!checked)
              }
            />
            <Label htmlFor="includeTransplant" className="text-forest-ink cursor-pointer font-medium">Include Transplant</Label>
          </div>

          {formData.includeTransplant && (
            <div className="space-y-6 pl-4 border-l-2 border-border-mist ml-2 mt-4">
              <div>
                <Label className="mb-3 block text-forest-ink font-medium">Transplant Type<RequiredMark /></Label>
                <Select
                  value={formData.transplantType}
                  onValueChange={(val) =>
                    handleInputChange('transplantType', val)
                  }
                >
                  <SelectTrigger className={SELECT_TRIGGER}>
                    <SelectValue placeholder="Select transplant type" className="text-forest-ink" />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                    <SelectItem value="autologous" className={SELECT_ITEM}>Autologous</SelectItem>
                    <SelectItem value="allogeneic" className={SELECT_ITEM}>Allogeneic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="block text-forest-ink font-medium mb-2">
                  Hospitalization Days: {formData.transplantDays}
                </Label>
                <div className="px-2">
                  <Slider
                    value={[formData.transplantDays]}
                    onValueChange={(val) =>
                      handleInputChange('transplantDays', val[0])
                    }
                    min={14}
                    max={60}
                    step={1}
                  />
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="w-6 h-6 text-forest-ink" />
        <h2 className="text-forest-ink">
          Diagnostics & Follow-up
        </h2>
      </div>

      <div className="space-y-6">
        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">PET-CT Scans: {formData.petCtCount}</Label>
          <div className="px-2">
            <Slider
              value={[formData.petCtCount]}
              onValueChange={(val) => handleInputChange('petCtCount', val[0])}
              min={0}
              max={10}
              step={1}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">MRI/CT Scans: {formData.mriCtCount}</Label>
          <div className="px-2">
            <Slider
              value={[formData.mriCtCount]}
              onValueChange={(val) => handleInputChange('mriCtCount', val[0])}
              min={0}
              max={20}
              step={1}
            />
          </div>
        </div>

        <div className="flex items-center space-x-3 py-3">
          <Checkbox
            id="includeNGS"
            checked={formData.includeNGS}
            onCheckedChange={(checked) =>
              handleInputChange('includeNGS', !!checked)
            }
          />
          <Label htmlFor="includeNGS" className="text-forest-ink cursor-pointer font-medium">Include NGS Panel Testing</Label>
        </div>

        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">
            OPD Consultations: {formData.opdConsults}
          </Label>
          <div className="px-2">
            <Slider
              value={[formData.opdConsults]}
              onValueChange={(val) => handleInputChange('opdConsults', val[0])}
              min={1}
              max={30}
              step={1}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-3">Follow-up Horizon<RequiredMark /></Label>
          <Select
            value={formData.followUpMonths.toString()}
            onValueChange={(val) =>
              handleInputChange('followUpMonths', parseInt(val, 10))
            }
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select follow-up duration" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="6" className={SELECT_ITEM}>6 Months</SelectItem>
              <SelectItem value="12" className={SELECT_ITEM}>12 Months</SelectItem>
              <SelectItem value="24" className={SELECT_ITEM}>24 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-6 h-6 text-forest-ink" />
        <h2 className="text-forest-ink">
          Insurance & Payment
        </h2>
      </div>

      <div className="space-y-5">
        <div>
          <Label className="mb-3 block text-forest-ink font-medium">How will you pay?<RequiredMark /></Label>
          <Select
            value={formData.paymentMethod}
            onValueChange={(val) => handleInputChange('paymentMethod', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select payment method" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="self_pay" className={SELECT_ITEM}>Self-pay</SelectItem>
              <SelectItem value="insurance" className={SELECT_ITEM}>I have insurance</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.paymentMethod === 'insurance' && (
          <div className="space-y-5 pl-6 mt-4">
            <div>
              <Label className="mb-3 block text-forest-ink font-medium">Insurance Provider<RequiredMark /></Label>
              <Select
                value={formData.insurer}
                onValueChange={(val) => handleInputChange('insurer', val)}
              >
                <SelectTrigger className={SELECT_TRIGGER}>
                  <SelectValue placeholder="Select insurance provider" className="text-forest-ink" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                  {availableInsurers.map((insurer) => (
                    <SelectItem key={insurer.id} value={insurer.id} className={SELECT_ITEM}>
                      {insurer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-3 block text-forest-ink font-medium">Policy Type<RequiredMark /></Label>
              <Select
                value={formData.policyType}
                onValueChange={(val) => handleInputChange('policyType', val)}
              >
                <SelectTrigger className={SELECT_TRIGGER}>
                  <SelectValue placeholder="Select policy type" className="text-forest-ink" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
                  <SelectItem value="domestic" className={SELECT_ITEM}>Domestic</SelectItem>
                  <SelectItem value="international" className={SELECT_ITEM}>International</SelectItem>
                  <SelectItem value="travel_medical" className={SELECT_ITEM}>Travel Medical</SelectItem>
                  <SelectItem value="corporate" className={SELECT_ITEM}>Corporate Group Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="customCoverage"
                checked={formData.customCoverage}
                onCheckedChange={(checked) =>
                  handleInputChange('customCoverage', !!checked)
                }
              />
              <Label htmlFor="customCoverage" className="text-forest-ink cursor-pointer font-medium">Customize coverage percentages</Label>
            </div>

            {formData.customCoverage && (
              <div className="space-y-5 mt-4">
                <div className="space-y-3">
                  <Label className="block text-forest-ink font-medium mb-2">
                    Inpatient Coverage: {formData.inpatientCoverage}%
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={[formData.inpatientCoverage]}
                      onValueChange={(val) =>
                        handleInputChange('inpatientCoverage', val[0])
                      }
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="block text-forest-ink font-medium mb-2">
                    Outpatient Coverage: {formData.outpatientCoverage}%
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={[formData.outpatientCoverage]}
                      onValueChange={(val) =>
                        handleInputChange('outpatientCoverage', val[0])
                      }
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="block text-forest-ink font-medium mb-2">
                    Drug Coverage: {formData.drugCoverage}%
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={[formData.drugCoverage]}
                      onValueChange={(val) =>
                        handleInputChange('drugCoverage', val[0])
                      }
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block text-forest-ink font-medium">
                    Annual Deductible ({selectedCountryData?.currency || formData.currency})
                  </Label>
                  <Input
                    type="number"
                    className={FIELD_INPUT}
                    value={formData.deductible}
                    onChange={(e) =>
                      handleInputChange(
                        'deductible',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>

                <div className="space-y-3">
                  <Label className="block text-forest-ink font-medium mb-2">
                    Co-pay Percentage: {formData.copayPercent}%
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={[formData.copayPercent]}
                      onValueChange={(val) =>
                        handleInputChange('copayPercent', val[0])
                      }
                      min={0}
                      max={50}
                      step={5}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Plane className="w-6 h-6 text-forest-ink" />
        <h2 className="text-forest-ink">
          Medical Tourism Extras
        </h2>
      </div>

      <div className="space-y-5">
        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">
            Number of Companions: {formData.companions}
          </Label>
          <div className="px-2">
            <Slider
              value={[formData.companions]}
              onValueChange={(val) => handleInputChange('companions', val[0])}
              min={0}
              max={5}
              step={1}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">
            Stay Duration (days): {formData.stayDuration}
          </Label>
          <div className="px-2">
            <Slider
              value={[formData.stayDuration]}
              onValueChange={(val) => handleInputChange('stayDuration', val[0])}
              min={7}
              max={180}
              step={1}
            />
          </div>
        </div>

        <div>
          <Label className="mb-3 block text-forest-ink font-medium">Accommodation Level<RequiredMark /></Label>
          <Select
            value={formData.accommodationLevel}
            onValueChange={(val) =>
              handleInputChange('accommodationLevel', val)
            }
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select accommodation" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="budget" className={SELECT_ITEM}>Budget</SelectItem>
              <SelectItem value="mid" className={SELECT_ITEM}>Mid-range</SelectItem>
              <SelectItem value="premium" className={SELECT_ITEM}>Premium</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-3 block text-forest-ink font-medium">Travel Type<RequiredMark /></Label>
          <Select
            value={formData.travelType}
            onValueChange={(val) => handleInputChange('travelType', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select travel class" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="economy" className={SELECT_ITEM}>Economy</SelectItem>
              <SelectItem value="premium" className={SELECT_ITEM}>Premium Economy</SelectItem>
              <SelectItem value="business" className={SELECT_ITEM}>Business</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label className="block text-forest-ink font-medium mb-2">
            Return Trips: {formData.returnTrips}
          </Label>
          <div className="px-2">
            <Slider
              value={[formData.returnTrips]}
              onValueChange={(val) => handleInputChange('returnTrips', val[0])}
              min={1}
              max={5}
              step={1}
            />
          </div>
        </div>

        <div>
          <Label className="mb-3 block text-forest-ink font-medium">Local Transport<RequiredMark /></Label>
          <Select
            value={formData.localTransport}
            onValueChange={(val) => handleInputChange('localTransport', val)}
          >
            <SelectTrigger className={SELECT_TRIGGER}>
              <SelectValue placeholder="Select transport" className="text-forest-ink" />
            </SelectTrigger>
            <SelectContent className={SELECT_CONTENT} position="popper" sideOffset={6}>
              <SelectItem value="daily_cab" className={SELECT_ITEM}>Daily Cab Service</SelectItem>
              <SelectItem value="public" className={SELECT_ITEM}>Public Transport</SelectItem>
              <SelectItem value="hospital_shuttle" className={SELECT_ITEM}>Hospital Shuttle</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block text-forest-ink">
            Complication Buffer: {formData.complicationBuffer}%
          </Label>
          <Slider
            value={[formData.complicationBuffer]}
            onValueChange={(val) =>
              handleInputChange('complicationBuffer', val[0])
            }
            min={0}
            max={30}
            step={5}
          />
        </div>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-6 h-6 text-forest-ink" />
        <h2 className="text-forest-ink">
          Cost Estimate
        </h2>
      </div>

      {costResult ? (
        <motion.div
          className="cost-results space-y-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="cost-total-card">
            <CardHeader className="mb-2">
              <CardTitle className="cost-total-card-label">
                Total Estimated Cost
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="cost-total-card-amount">
                {displayCurrencySymbol}{formatMoney(costResult.totalCostLocal)}
                <span className="cost-total-card-ccy">({displayCurrency})</span>
              </div>
              <div className="cost-total-card-sub">
                ≈ USD ${formatMoney(costResult.totalCostUSD)}
              </div>
              {costResult.exchangeRateToUSD && costResult.exchangeRateToUSD !== 1.0 && (
                <div className="cost-total-card-sub" style={{ marginTop: 8 }}>
                  Exchange rate: 1 USD = {costResult.exchangeRateToUSD} {displayCurrency} (reference rate)
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-paper border-hair rounded-2xl">
            <CardHeader>
              <CardTitle className="text-forest-ink" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>Cost Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-forest-ink">Clinical Cost</span>
                <div className="text-right">
                  <div className="text-forest-ink font-semibold">
                    {displayCurrencySymbol}{formatMoney(costResult.clinicalCost)}
                  </div>
                  <div className="text-xs text-[#3d4a42]">
                    ≈ USD ${formatMoney(costResult.clinicalCostUSD)}
                  </div>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-forest-ink">Non-clinical Cost</span>
                <div className="text-right">
                  <div className="text-forest-ink font-semibold">
                    {displayCurrencySymbol}{formatMoney(costResult.nonClinicalCost)}
                  </div>
                  <div className="text-xs text-[#3d4a42]">
                    ≈ USD ${formatMoney(costResult.nonClinicalCostUSD)}
                  </div>
                </div>
              </div>
              <Separator className="bg-hair" />
              <div className="flex justify-between font-semibold">
                <span className="text-forest-ink">Insurance Pays</span>
                <div className="text-right">
                  <div className="text-forest-ink">
                    -{displayCurrencySymbol}{formatMoney(costResult.insurancePays)}
                  </div>
                  <div className="text-xs text-[#3d4a42]">
                    ≈ -USD ${formatMoney(costResult.insurancePaysUSD)}
                  </div>
                </div>
              </div>
              <Separator className="bg-hair" />
              <div className="flex justify-between text-xl font-semibold">
                <span className="text-forest-ink">Your Out-of-Pocket</span>
                <div className="text-right">
                  <div className="text-forest-ink">
                    {displayCurrencySymbol}{formatMoney(costResult.patientOutOfPocket)}
                  </div>
                  <div className="text-sm text-[#3d4a42]">
                    ≈ USD ${formatMoney(costResult.patientOutOfPocketUSD)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-paper border-hair rounded-2xl">
            <CardHeader>
              <CardTitle className="text-forest-ink" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>Detailed Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {costResult.breakdown && typeof costResult.breakdown === 'object' ? (
                Object.entries(costResult.breakdown)
                  .filter(([key, value]) => safeNumber(value) > 0)
                  .map(([key, value]) => {
                    const usdValue = costResult.breakdownUSD?.[key] || 0;
                    return (
                      <div key={key} className="flex justify-between items-center">
                        <span className="text-forest-ink capitalize">
                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                        </span>
                        <div className="text-right">
                          <div className="text-forest-ink font-semibold">
                            {displayCurrencySymbol}{formatMoney(value)}
                          </div>
                          {usdValue > 0 && (
                            <div className="text-xs text-[#3d4a42]">
                              ≈ USD ${formatMoney(usdValue)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-[#3d4a42] text-sm">No detailed breakdown available</p>
              )}
            </CardContent>
          </Card>

          {costResult.assumptions && costResult.assumptions.length > 0 && (
            <Card className="bg-paper border-hair rounded-2xl">
              <CardHeader>
                <CardTitle className="text-forest-ink" style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>Key Assumptions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {costResult.assumptions.map((a, idx) => (
                  <p key={idx} className={`text-forest-ink ${a.includes('⚠️') ? 'font-medium' : ''}`}>
                    • {a}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}
        </motion.div>
      ) : (
        <Card className="bg-paper border-hair rounded-2xl">
          <CardContent className="p-6 text-center">
            <p className="text-charcoal">No cost estimate available. Please calculate to see results.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    if (step === 1) return renderStep1();
    if (step === 2) return renderStep2();
    if (step === 3) return renderStep3();
    if (step === 4) return renderStep4();
    if (step === 5) return renderStep5();
    if (step === 6) return renderStep6();
    return renderResults();
  };

  const handleNext = () => {
    const missing = getStepIssues(formData, step);
    if (missing.length) {
      setMissingItems(missing);
      setErrorMessage('');
      return;
    }
    setMissingItems([]);
    setErrorMessage('');
    if (step < INPUT_STEPS) {
      setStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setMissingItems([]);
    setErrorMessage('');
    if (step > 1 && step <= INPUT_STEPS) {
      setStep((prev) => prev - 1);
    }
  };

  const progressWidth = `${(Math.min(step, INPUT_STEPS) / INPUT_STEPS) * 100}%`;

  return (
    <>
    <style>{`
      .cost-segment [role="tab"] {
        min-width: 0 !important;
        min-height: 40px !important;
        padding: 0 8px !important;
        color: #0f3e17 !important;
        background: transparent !important;
        box-shadow: none !important;
      }
      .cost-segment [role="tab"][data-state="active"] {
        background: #ffffff !important;
        color: #0f3e17 !important;
        box-shadow: 0 1px 2px rgba(14, 17, 22, 0.08) !important;
      }
      .cost-preview {
        position: relative;
        width: 100%;
        min-height: 420px;
        overflow: hidden;
        border-radius: 28px;
        border: 1px solid #E8ECF2;
        background:
          radial-gradient(ellipse 70% 50% at 50% 42%, rgba(225, 244, 223, 0.9) 0%, rgba(246, 248, 251, 0.4) 46%, #f3f5f2 100%);
      }
      @media (min-width: 1024px) {
        .cost-preview { min-height: 640px; }
      }
      .cost-preview-copy {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 420px;
        height: 100%;
        padding: 28px 28px 32px;
      }
      @media (min-width: 1024px) {
        .cost-preview-copy { min-height: 640px; }
      }
      .cost-kicker {
        margin: 0;
        font-size: 11px;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: #3d4a42;
        font-family: "Instrument Sans", system-ui, sans-serif;
      }
      .cost-results h2,
      .cost-results h3,
      .page-shell .cost-results h2,
      .page-shell .cost-results h3 {
        font-family: "Instrument Sans", system-ui, sans-serif !important;
      }
      .cost-results .bg-forest-ink h3 {
        color: #fffefc !important;
      }
      .cost-total-card {
        background: #0f3e17 !important;
        border: 1px solid #0f3e17 !important;
        border-radius: 24px !important;
        padding: 24px 28px !important;
        overflow: visible !important;
        box-sizing: border-box;
      }
      .cost-total-card-label {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #fffefc !important;
        font-family: "Instrument Sans", system-ui, sans-serif !important;
      }
      .cost-total-card-amount {
        margin: 0 0 8px;
        font-size: clamp(1.75rem, 5vw, 2.75rem);
        font-weight: 300;
        letter-spacing: -0.03em;
        line-height: 1.2;
        color: #fffefc;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .cost-total-card-ccy {
        margin-left: 8px;
        font-size: 1.05rem;
        color: #cfe7d3;
        white-space: nowrap;
      }
      .cost-total-card-sub {
        font-size: 1rem;
        color: #cfe7d3;
        overflow-wrap: anywhere;
      }
      .cost-title,
      .page-shell .cost-title {
        margin: 10px 0 0;
        font-size: 1.375rem;
        line-height: 1.25;
        letter-spacing: -0.03em;
        font-weight: 600;
        font-family: "Instrument Sans", system-ui, sans-serif !important;
        color: #0f3e17;
      }
      .cost-notice {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px 18px;
        border-radius: 20px;
        background: #fffefc;
        border: 1px solid #E8ECF2;
        box-shadow: 0 10px 28px rgba(14, 17, 22, 0.06);
        color: #0f3e17;
      }
      .cost-notice-icon {
        width: 20px;
        height: 20px;
        flex: none;
        margin-top: 1px;
        color: #0f3e17;
      }
      .cost-notice-body { min-width: 0; }
      .cost-notice-title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: #0f3e17;
        font-family: "Instrument Sans", system-ui, sans-serif;
      }
      .cost-notice-detail {
        margin: 4px 0 0;
        font-size: 14px;
        line-height: 1.45;
        color: #3d4a42;
      }
      .cost-notice-list {
        margin: 10px 0 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 6px;
      }
      .cost-notice-list li {
        position: relative;
        padding-left: 14px;
        font-size: 14px;
        line-height: 1.4;
        color: #0f3e17;
      }
      .cost-notice-list li::before {
        content: "";
        position: absolute;
        left: 0;
        top: 8px;
        width: 5px;
        height: 5px;
        border-radius: 999px;
        background: #0f3e17;
      }
      .cost-meta {
        margin: 6px 0 0;
        font-size: 14px;
        color: #3d4a42;
        font-family: "Instrument Sans", system-ui, sans-serif;
      }
      .cost-preview-foot { display: grid; gap: 16px; }
      .cost-chips { display: flex; flex-wrap: wrap; gap: 8px; }
      .cost-chip {
        border-radius: 999px;
        background: #fffefc;
        border: 1px solid #E8ECF2;
        padding: 6px 12px;
        font-size: 13px;
        color: #0f3e17;
      }
      .cost-hint {
        margin: 0;
        max-width: 22rem;
        font-size: 14px;
        line-height: 1.5;
        color: #5b6470;
      }
      .cost-total {
        margin: 6px 0 0;
        font-size: 40px;
        font-weight: 300;
        letter-spacing: -0.04em;
        color: #0f3e17;
      }
      .cost-stage {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        perspective: 1100px;
        pointer-events: none;
      }
      .cost-wash {
        position: absolute;
        width: 340px;
        height: 340px;
        border-radius: 9999px;
        background: radial-gradient(circle, rgba(177, 219, 184, 0.42) 0%, rgba(243, 245, 242, 0) 68%);
        filter: blur(12px);
      }
      .cost-atmosphere {
        position: absolute;
        width: 210px;
        height: 210px;
        border-radius: 9999px;
        background: radial-gradient(circle, rgba(255,255,255,0.08) 40%, rgba(177,219,184,0.35) 62%, rgba(243,245,242,0) 74%);
        filter: blur(1px);
      }
      .cost-orb {
        position: relative;
        width: 168px;
        height: 168px;
        border-radius: 9999px;
        transform-style: preserve-3d;
        box-shadow:
          0 28px 50px rgba(15, 62, 23, 0.18),
          0 8px 18px rgba(15, 62, 23, 0.08);
      }
      .cost-orb-core,
      .cost-orb-map,
      .cost-orb-shade,
      .cost-orb-gloss,
      .cost-orb-rim {
        position: absolute;
        inset: 0;
        border-radius: inherit;
      }
      .cost-orb-core {
        background:
          radial-gradient(circle at 32% 26%, #f7fff7 0%, #cfe8d1 26%, #6fa87a 58%, #123f1c 100%);
      }
      .cost-orb-map {
        background-image:
          radial-gradient(ellipse 42px 22px at 22% 38%, rgba(15, 62, 23, 0.28) 0 58%, transparent 60%),
          radial-gradient(ellipse 50px 28px at 58% 30%, rgba(15, 62, 23, 0.24) 0 60%, transparent 62%),
          radial-gradient(ellipse 36px 18px at 78% 56%, rgba(15, 62, 23, 0.22) 0 58%, transparent 60%),
          radial-gradient(ellipse 46px 20px at 40% 72%, rgba(15, 62, 23, 0.2) 0 58%, transparent 60%),
          radial-gradient(ellipse 20px 12px at 14% 62%, rgba(15, 62, 23, 0.18) 0 55%, transparent 57%);
        background-size: 220% 100%;
        animation: cost-spin-map 22s linear infinite;
        mix-blend-mode: multiply;
        opacity: 0.85;
      }
      .cost-orb-shade {
        background:
          radial-gradient(circle at 78% 72%, rgba(8, 24, 12, 0.42) 0%, rgba(8, 24, 12, 0.08) 42%, transparent 68%),
          linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 46%);
      }
      .cost-orb-gloss {
        background:
          radial-gradient(ellipse 52% 28% at 30% 24%, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.18) 42%, transparent 68%);
      }
      .cost-orb-rim {
        box-shadow:
          inset -16px -18px 26px rgba(10, 32, 16, 0.28),
          inset 10px 12px 16px rgba(255,255,255,0.38),
          inset 0 0 0 1px rgba(255,255,255,0.28);
      }
      .cost-contact {
        position: absolute;
        bottom: 22%;
        width: 150px;
        height: 22px;
        border-radius: 9999px;
        background: radial-gradient(ellipse, rgba(15, 62, 23, 0.18) 0%, transparent 70%);
        filter: blur(4px);
      }
      .cost-ring {
        position: absolute;
        border-radius: 9999px;
        border: 1.5px solid rgba(15, 62, 23, 0.2);
        transform-style: preserve-3d;
        box-shadow: 0 0 0 1px rgba(255,255,255,0.15) inset;
      }
      .cost-ring-1 { width: 248px; height: 88px; animation: cost-orbit 11s linear infinite; }
      .cost-ring-2 { width: 318px; height: 116px; border-color: rgba(15, 62, 23, 0.13); animation: cost-orbit 17s linear infinite reverse; }
      .cost-ring-3 { width: 386px; height: 142px; border-color: rgba(15, 62, 23, 0.08); animation: cost-orbit 24s linear infinite; }
      @keyframes cost-orbit {
        from { transform: rotateX(70deg) rotateZ(0deg); }
        to { transform: rotateX(70deg) rotateZ(360deg); }
      }
      @keyframes cost-spin-map {
        from { background-position: 0 0; }
        to { background-position: 220% 0; }
      }
    `}</style>
    <div className="care-page min-h-screen bg-cream-paper pt-8 sm:pt-10 pb-8 sm:pb-12 px-4 sm:px-6">
      <MetaTags
        title="Cancer Treatment Cost Calculator (USD & INR)"
        description="Estimate cancer treatment costs across US and global centers. Compare surgery, chemotherapy, radiation, and inpatient stay costs deterministically."
        path="/cost-calculator"
      />
      <div className="max-w-7xl mx-auto">
        <CarePageHeader
          current="/cost-calculator"
          kicker="Cost calculator"
          title="Treatment cost calculator"
          lede="Country-level estimates for cancer treatment, not tied to a named hospital. Actual packages vary."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >

            {/* Progress Indicator */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-2 gap-1 sm:gap-2">
                {[1, 2, 3, 4, 5, 6].map((s) => (
                  <div
                    key={s}
                    className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold transition-all text-sm ${
                      step >= s
                        ? 'bg-forest-ink text-cream-paper'
                        : 'bg-mist text-charcoal border border-hair'
                    } ${step === s ? 'ring-2 ring-forest-ink/20' : ''}`}
                  >
                    {s}
                  </div>
                ))}
              </div>
              <div className="h-1 bg-hair rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-forest-ink rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: progressWidth }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            {(missingItems.length > 0 || errorMessage) && (
              <motion.div
                className="mb-5"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <CostNotice
                  title={missingItems.length ? 'A few details are still needed' : "We couldn't calculate just yet"}
                  items={missingItems}
                  detail={missingItems.length ? 'Finish these, then continue.' : errorMessage}
                />
              </motion.div>
            )}

            {/* Form Content */}
            <Card className="bg-paper border-hair rounded-3xl p-0 overflow-visible">
              <CardContent className="p-5 sm:p-7">
                {renderCurrentStep()}

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6 sm:mt-8">
                  {step > 1 && step <= INPUT_STEPS && (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-hair text-forest-ink hover:bg-mist min-h-[44px] w-full sm:w-auto text-sm px-5 rounded-2xl"
                      onClick={handlePrevious}
                      disabled={isLoading}
                    >
                      Previous
                    </Button>
                  )}

                  {step < INPUT_STEPS && (
                    <Button
                      type="button"
                      className="sm:ml-auto bg-forest-ink hover:bg-forest-shadow text-cream-paper min-h-[44px] w-full sm:w-auto text-sm px-5 rounded-2xl"
                      onClick={handleNext}
                      disabled={isLoading}
                    >
                      Next
                    </Button>
                  )}

                  {step === INPUT_STEPS && (
                    <Button
                      type="button"
                      className="ml-auto bg-forest-ink hover:bg-forest-shadow text-cream-paper min-h-[44px] w-full sm:w-auto text-sm px-5 rounded-2xl"
                      onClick={calculateCost}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Calculating…' : 'Calculate Cost'}
                    </Button>
                  )}

                  {step === 7 && (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-hair text-forest-ink hover:bg-mist min-h-[44px] w-full sm:w-auto text-sm px-5 rounded-2xl"
                      onClick={resetCalculator}
                      disabled={isLoading}
                    >
                      Start New Calculation
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            className="sticky top-4 sm:top-24 mt-8 lg:mt-0"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <CostPreviewPanel
              formData={formData}
              costResult={costResult}
              selectedCountryData={selectedCountryData}
            />
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CostCalculator;
